import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { LogOut, Mail, Phone, Calendar, User as UserIcon, MessageSquare, Loader2, ShieldAlert } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(location.state?.user || null);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const init = async () => {
      try {
        if (!user) {
          const meRes = await axios.get(`${API}/auth/me`, { withCredentials: true });
          setUser(meRes.data);
          if (meRes.data.role !== 'admin') {
            setError('Non hai i permessi per accedere a questa area.');
            setLoading(false);
            return;
          }
        } else if (user.role !== 'admin') {
          setError('Non hai i permessi per accedere a questa area.');
          setLoading(false);
          return;
        }

        const contactsRes = await axios.get(`${API}/contacts/`, { withCredentials: true });
        setContacts(contactsRes.data);
      } catch (err) {
        if (err.response?.status === 401) {
          navigate('/login');
        } else if (err.response?.status === 403) {
          setError('Non hai i permessi per accedere a questa area.');
        } else {
          setError('Errore nel caricamento dei dati.');
        }
      } finally {
        setLoading(false);
      }
    };
    init();
    // eslint-disable-next-line
  }, []);

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (err) {
      console.error('Logout error:', err);
    }
    navigate('/login');
  };

  const formatDate = (dateStr) => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleString('it-IT', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      });
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#2A2520] flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#2A2520] flex items-center justify-center px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md w-full bg-[#F5F1E8]/5 border border-red-500/30 p-10 text-center"
        >
          <ShieldAlert className="w-14 h-14 text-red-400 mx-auto mb-4" />
          <h2 className="text-2xl font-serif text-[#F5F1E8] mb-3">Accesso Negato</h2>
          <p className="text-[#F5F1E8]/70 mb-6">{error}</p>
          <button
            onClick={handleLogout}
            className="bg-[#C9826B] text-[#F5F1E8] px-6 py-3 tracking-widest uppercase text-sm hover:bg-[#D4AF37] transition-colors duration-300"
            data-testid="logout-forbidden-btn"
          >
            Esci
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#2A2520]">
      <header className="bg-[#2A2520] border-b border-[#F5F1E8]/10 sticky top-0 z-20 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-serif text-[#F5F1E8]">Area Admin</h1>
            <p className="text-[#D4AF37] text-xs tracking-widest uppercase">
              Ca' del Sole Italia
            </p>
          </div>
          <div className="flex items-center gap-4">
            {user && (
              <div className="hidden md:flex items-center gap-3">
                {user.picture && (
                  <img
                    src={user.picture}
                    alt={user.name}
                    className="w-9 h-9 rounded-full border border-[#D4AF37]/40"
                  />
                )}
                <div className="text-right">
                  <p className="text-[#F5F1E8] text-sm">{user.name}</p>
                  <p className="text-[#F5F1E8]/50 text-xs">{user.email}</p>
                </div>
              </div>
            )}
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 bg-[#F5F1E8]/5 hover:bg-[#C9826B] border border-[#F5F1E8]/20 hover:border-[#C9826B] text-[#F5F1E8] px-4 py-2 text-sm transition-all duration-300"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Esci</span>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-10"
        >
          <div className="bg-[#F5F1E8]/5 backdrop-blur-sm border border-[#F5F1E8]/10 p-6 inline-block">
            <p className="text-[#D4AF37] text-xs tracking-widest uppercase mb-2">
              Richieste Totali
            </p>
            <p className="text-4xl font-serif text-[#F5F1E8]" data-testid="total-contacts-count">{contacts.length}</p>
          </div>
        </motion.div>

        <h2 className="text-2xl font-serif text-[#F5F1E8] mb-6">
          Richieste di Contatto
        </h2>

        {contacts.length === 0 ? (
          <div className="text-center py-16 bg-[#F5F1E8]/5 border border-[#F5F1E8]/10">
            <MessageSquare className="w-12 h-12 text-[#F5F1E8]/30 mx-auto mb-4" />
            <p className="text-[#F5F1E8]/60">Nessuna richiesta ricevuta al momento.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {contacts.map((contact, index) => (
              <motion.div
                key={contact.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-[#F5F1E8]/5 backdrop-blur-sm border border-[#F5F1E8]/10 hover:border-[#D4AF37]/40 p-6 transition-all duration-300"
                data-testid={`contact-card-${contact.id}`}
              >
                <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                      <UserIcon className="w-5 h-5 text-[#D4AF37]" />
                    </div>
                    <div>
                      <h3 className="text-[#F5F1E8] font-semibold">{contact.name}</h3>
                      <p className="text-[#F5F1E8]/50 text-xs flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {formatDate(contact.created_at)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="bg-[#2C5F4F]/30 text-[#D4AF37] text-xs px-3 py-1 uppercase tracking-wider">
                      {contact.language}
                    </span>
                    <span className={`text-xs px-3 py-1 uppercase tracking-wider ${
                      contact.status === 'new' ? 'bg-[#C9826B]/20 text-[#C9826B]' : 'bg-[#2C5F4F]/30 text-[#F5F1E8]'
                    }`}>
                      {contact.status}
                    </span>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-3 mb-4">
                  <a
                    href={`mailto:${contact.email}`}
                    className="flex items-center gap-2 text-[#F5F1E8]/80 hover:text-[#D4AF37] transition-colors duration-200"
                  >
                    <Mail className="w-4 h-4" />
                    <span className="text-sm">{contact.email}</span>
                  </a>
                  {contact.phone && (
                    <a
                      href={`tel:${contact.phone}`}
                      className="flex items-center gap-2 text-[#F5F1E8]/80 hover:text-[#D4AF37] transition-colors duration-200"
                    >
                      <Phone className="w-4 h-4" />
                      <span className="text-sm">{contact.phone}</span>
                    </a>
                  )}
                </div>

                <div className="bg-[#2A2520]/50 border-l-2 border-[#D4AF37] p-4">
                  <p className="text-[#F5F1E8]/90 text-sm leading-relaxed whitespace-pre-wrap">
                    {contact.message}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPage;
